import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type {
  CreditAnalysis,
  CreditAnalysisHistory,
  CreditAnalysisStatus,
} from '@/api/commercial/credit-analysis';
import {
  CreditAnalysisStatus as CreditAnalysisStatusEnum,
  creditAnalysisApi,
} from '@/api/commercial/credit-analysis';
import { useToast } from '@/hooks/use-toast';
import { centsToPtbr, ptbrToCents } from '@/lib/formatters';

export type Situation = 'APPROVED' | 'PARTIALLY_APPROVED' | 'DECLINED';

interface Props {
  creditAnalysis: CreditAnalysis | null;
  onSuccess?: () => void;
  allDocumentsReviewed?: boolean;
}

export function useCreditAnalysisUpdateForm({
  creditAnalysis,
  onSuccess,
  allDocumentsReviewed = false,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: historyData } = useQuery({
    queryKey: ['creditAnalysisHistory', creditAnalysis?.accountId],
    queryFn: async () => {
      if (!creditAnalysis?.accountId) return { docs: [] };
      return creditAnalysisApi.getHistoryByAccount(creditAnalysis.accountId, 1, 1);
    },
    enabled: !!creditAnalysis?.accountId,
  });

  const hasPreviousAnalysis =
    historyData?.docs?.some(
      (h: CreditAnalysisHistory) =>
        h.id !== creditAnalysis?.id &&
        ['APPROVED', 'PARTIALLY_APPROVED', 'DECLINED'].includes(h.status),
    ) ?? false;

  // STATES
  const [situation, setSituation] = useState<Situation | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [creditLimit, setCreditLimit] = useState('');
  const [newAnalysisIn, setNewAnalysisIn] = useState('');
  const [nextPurchase, setNextPurchase] = useState(false);
  const [observations, setObservations] = useState('');
  const [isUnlimited, setIsUnlimited] = useState(false);

  // LOAD INITIAL DATA
  useEffect(() => {
    if (!creditAnalysis) return;

    if (creditAnalysis.paymentMethods?.length) {
      setPaymentMethod(creditAnalysis.paymentMethods[0]);
    }

    const hasNoLimit =
      !creditAnalysis.creditLimitInCents || creditAnalysis.creditLimitInCents === 0;

    const isApproved =
      creditAnalysis.status === 'APPROVED' || creditAnalysis.status === 'PARTIALLY_APPROVED';

    setIsUnlimited(isApproved && hasNoLimit);

    if (creditAnalysis.creditLimitInCents) {
      setCreditLimit(centsToPtbr(creditAnalysis.creditLimitInCents));
    }

    setNextPurchase(creditAnalysis.newAnalysisIn === 'NEXT_PURCHASE');

    if (creditAnalysis.newAnalysisIn && creditAnalysis.newAnalysisIn !== 'NEXT_PURCHASE') {
      setNewAnalysisIn(new Date(creditAnalysis.newAnalysisIn).toISOString().split('T')[0]);
    }

    setObservations(creditAnalysis.observations || '');
  }, [creditAnalysis]);

  const isApproved = situation === 'APPROVED' || situation === 'PARTIALLY_APPROVED';
  const isDeclined = situation === 'DECLINED';

  const isWaitingForClientCorrection =
    creditAnalysis?.status === CreditAnalysisStatusEnum.PENDING_EXTERNAL_DOCS;

  const canSubmit =
    !isWaitingForClientCorrection &&
    creditAnalysis?.status &&
    [CreditAnalysisStatusEnum.IN_ANALYSIS, CreditAnalysisStatusEnum.PENDING_ANALYSIS].includes(
      creditAnalysis.status as CreditAnalysisStatus,
    ) &&
    (hasPreviousAnalysis || allDocumentsReviewed);

  // VALIDATION
  const getValidationError = () => {
    if (!situation) return 'Selecione a situação';
    if (!observations.trim()) return 'Parecer obrigatório';
    if (!nextPurchase && !newAnalysisIn) return 'Informe próxima análise';

    if (isApproved) {
      if (!paymentMethod) return 'Selecione forma de pagamento';
      if (!isUnlimited && (!creditLimit || ptbrToCents(creditLimit) <= 0)) {
        return 'Informe limite ou marque ilimitado';
      }
    }

    return null;
  };

  // MUTATIONS
  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!creditAnalysis?.id) throw new Error('ID obrigatório');

      return creditAnalysisApi.approve(creditAnalysis.id, {
        creditLimitInCents: isUnlimited ? null : ptbrToCents(creditLimit),
        isUnlimited,
        paymentMethods: paymentMethod ? [paymentMethod] : [],
        observations,
        newAnalysisIn: nextPurchase ? 'NEXT_PURCHASE' : new Date(newAnalysisIn).toISOString(),
      });
    },
    onSuccess: () => {
      toast({ title: 'Aprovado com sucesso' });
      queryClient.invalidateQueries({ queryKey: ['credit-analysis'] });
      onSuccess?.();
    },
  });

  const declineMutation = useMutation({
    mutationFn: async () => {
      if (!creditAnalysis?.id) throw new Error('ID obrigatório');

      return creditAnalysisApi.decline(creditAnalysis.id, {
        observations,
        newAnalysisIn: nextPurchase ? 'NEXT_PURCHASE' : new Date(newAnalysisIn).toISOString(),
      });
    },
    onSuccess: () => {
      toast({ title: 'Reprovado' });
      queryClient.invalidateQueries({ queryKey: ['credit-analysis'] });
      onSuccess?.();
    },
  });

  const handleApprove = () => {
    const error = getValidationError();
    if (error) {
      toast({ title: 'Erro', description: error, variant: 'destructive' });
      return;
    }
    approveMutation.mutate();
  };

  const handleDecline = () => {
    const error = getValidationError();
    if (error) {
      toast({ title: 'Erro', description: error, variant: 'destructive' });
      return;
    }
    declineMutation.mutate();
  };

  const reset = () => {
    setSituation(null);
    setPaymentMethod(null);
    setCreditLimit('');
    setNewAnalysisIn('');
    setNextPurchase(false);
    setObservations('');
    setIsUnlimited(false);
  };

  return {
    // states
    situation,
    paymentMethod,
    creditLimit,
    newAnalysisIn,
    nextPurchase,
    observations,
    isUnlimited,

    // setters
    setSituation,
    setPaymentMethod,
    setCreditLimit,
    setNewAnalysisIn,
    setNextPurchase,
    setObservations,
    setIsUnlimited,

    // computed
    isApproved,
    isDeclined,
    canSubmit,
    isSubmitting: approveMutation.isPending || declineMutation.isPending,

    // actions
    handleApprove,
    handleDecline,
    reset,
  };
}
